import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { PROPERTY_CLASSES, straightLineSchedule, type CreateFixedAssetInput, type PropertyClass } from '@accounting/shared';
import { useCreateFixedAsset } from '../../hooks/useFixedAssets';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/fixed-assets/new')({
  component: NewFixedAssetPage,
});

function NewFixedAssetPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: accounts = [] } = useAccounts(companyId);
  const create = useCreateFixedAsset(companyId);

  const isAssetSelectable = (a: typeof accounts[number]) =>
    a.isActive && a.allowDirectPosting && a.accountType === 'asset';
  const isExpenseSelectable = (a: typeof accounts[number]) =>
    a.isActive && a.allowDirectPosting && (a.accountType === 'expense' || a.accountType === 'other_expense');
  const byCode = (code: string) => accounts.find((a) => a.code === code)?.id ?? '';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetAccountId, setAssetAccountId] = useState('');
  const [accumDepAccountId, setAccumDepAccountId] = useState('');
  const [depExpenseAccountId, setDepExpenseAccountId] = useState('');
  const [cost, setCost] = useState('');
  const [salvageValue, setSalvageValue] = useState('0');
  const [inServiceDate, setInServiceDate] = useState(todayIso());
  const [usefulLifeMonths, setUsefulLifeMonths] = useState('60');

  // Default the GL accounts to the standard fixed-asset codes once loaded.
  useEffect(() => {
    if (accounts.length === 0) return;
    setAssetAccountId((v) => v || byCode('1500'));
    setAccumDepAccountId((v) => v || byCode('1510'));
    setDepExpenseAccountId((v) => v || byCode('8100'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  // Property type picks the life + convention; the convention is what gets saved.
  const [propertyClass, setPropertyClass] = useState<PropertyClass>('general');
  const [autoPost, setAutoPost] = useState(true);
  const convention = PROPERTY_CLASSES[propertyClass].convention;
  const choosePropertyClass = (pc: PropertyClass) => {
    setPropertyClass(pc);
    const preset = PROPERTY_CLASSES[pc].usefulLifeMonths;
    if (preset) {
      setUsefulLifeMonths(String(preset));
      setSalvageValue('0');
    }
  };

  // Live straight-line depreciation projection (same math the server uses).
  const costN = parseFloat(cost) || 0;
  const salvageN = parseFloat(salvageValue) || 0;
  const lifeN = Number(usefulLifeMonths) || 0;
  const preview = useMemo(() => {
    if (!(costN > 0 && lifeN >= 1 && inServiceDate)) return null;
    const schedule = straightLineSchedule(String(costN), String(salvageN), lifeN, inServiceDate, convention);
    if (schedule.length === 0) return null;
    // Under mid-month the first row is a half month; the full monthly amount is the second.
    const monthly = (convention === 'mid_month' ? schedule[1] ?? schedule[0] : schedule[0])!.amount;
    return { schedule, monthly, firstMonth: schedule[0]!.amount, depreciable: costN - salvageN };
  }, [costN, salvageN, lifeN, inServiceDate, convention]);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Go to Companies</Link>
      </div>
    );
  }

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Could not create asset'
      : create.error?.message;

  const valid =
    name.trim() && assetAccountId && accumDepAccountId && depExpenseAccountId &&
    parseFloat(cost) > 0 && Number(usefulLifeMonths) >= 1;

  const submit = () => {
    const input: CreateFixedAssetInput = {
      name: name.trim(),
      description: description.trim() || undefined,
      assetAccountId,
      accumDepAccountId,
      depExpenseAccountId,
      cost: String(parseFloat(cost) || 0),
      salvageValue: String(parseFloat(salvageValue) || 0),
      inServiceDate,
      usefulLifeMonths: Number(usefulLifeMonths) || 1,
      method: 'straight_line',
      convention,
      autoPost,
    };
    create.mutate(input, { onSuccess: (a) => void navigate({ to: '/fixed-assets/$id', params: { id: a.id } }) });
  };

  const sel = (val: string, set: (v: string) => void, pred: (a: typeof accounts[number]) => boolean) => (
    <div style={{ maxWidth: 320 }}>
      <AccountPicker accounts={accounts} value={val} onChange={set} predicate={pred} />
    </div>
  );

  return (
    <div>
      <Link to="/fixed-assets" style={{ fontSize: 13 }}>← Fixed Assets</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>New fixed asset</h1>

      <div className="card" style={{ padding: 16, maxWidth: 720 }}>
        <div className="form-grid">
          <label htmlFor="fa-name">Name</label>
          <input id="fa-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />

          <label htmlFor="fa-desc">Description</label>
          <input id="fa-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />

          <label htmlFor="fa-class">Property type</label>
          <select id="fa-class" value={propertyClass} onChange={(e) => choosePropertyClass(e.target.value as PropertyClass)} style={{ maxWidth: 320 }}>
            {(Object.keys(PROPERTY_CLASSES) as PropertyClass[]).map((k) => (
              <option key={k} value={k}>{PROPERTY_CLASSES[k].label}</option>
            ))}
          </select>

          <label htmlFor="fa-cost">Cost</label>
          <input id="fa-cost" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" style={{ maxWidth: 180, textAlign: 'right' }} />

          <label htmlFor="fa-salv">Salvage value</label>
          <input id="fa-salv" inputMode="decimal" value={salvageValue} onChange={(e) => setSalvageValue(e.target.value)} style={{ maxWidth: 180, textAlign: 'right' }} />

          <label htmlFor="fa-svc">In-service date</label>
          <input id="fa-svc" type="date" value={inServiceDate} onChange={(e) => setInServiceDate(e.target.value)} style={{ maxWidth: 200 }} />

          <label htmlFor="fa-life">Useful life (months)</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input id="fa-life" inputMode="numeric" value={usefulLifeMonths} onChange={(e) => setUsefulLifeMonths(e.target.value)} style={{ maxWidth: 120 }} />
            <span className="muted" style={{ fontSize: 12 }}>
              {convention === 'mid_month'
                ? 'Mid-month convention: the in-service month and the final month each count as half.'
                : 'Full-month convention: the whole in-service month counts.'}
            </span>
          </div>

          <label htmlFor="fa-auto">Auto-post</label>
          <label htmlFor="fa-auto" style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 400 }}>
            <input id="fa-auto" type="checkbox" checked={autoPost} onChange={(e) => setAutoPost(e.target.checked)} />
            Post each month's depreciation automatically once the month closes (nightly).
          </label>

          <label>Asset account</label>
          {sel(assetAccountId, setAssetAccountId, isAssetSelectable)}
          <label>Accumulated depreciation</label>
          {sel(accumDepAccountId, setAccumDepAccountId, isAssetSelectable)}
          <label>Depreciation expense</label>
          {sel(depExpenseAccountId, setDepExpenseAccountId, isExpenseSelectable)}
        </div>

        <p className="muted" style={{ fontSize: 12 }}>
          The asset's cost should already be in the asset account (from the check/bill that bought it).
          This register tracks it for depreciation & disposal.
          {propertyClass !== 'general' && ' Buildings only: land is never depreciated, so keep the land portion on its own asset account.'}
        </p>

        {errorMessage && <div className="field-error" style={{ marginTop: 8 }}>{errorMessage}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn-primary" onClick={submit} disabled={!valid || create.isPending}>
            {create.isPending ? 'Saving…' : 'Save asset'}
          </button>
          <Link to="/fixed-assets" className="btn-secondary" style={{ textDecoration: 'none' }}>Cancel</Link>
        </div>
      </div>

      {preview && (
        <div className="card" style={{ padding: 16, maxWidth: 720, marginTop: 16 }}>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>Projected depreciation</h2>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Monthly</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(preview.monthly)}</div>
            </div>
            {convention === 'mid_month' && (
              <div>
                <div className="muted" style={{ fontSize: 12 }}>First month (half)</div>
                <div style={{ fontSize: 18 }}>{formatCurrency(preview.firstMonth)}</div>
              </div>
            )}
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Periods</div>
              <div style={{ fontSize: 18 }}>{preview.schedule.length}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Depreciable base</div>
              <div style={{ fontSize: 18 }}>{formatCurrency(preview.depreciable)}</div>
            </div>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Period end</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th style={{ textAlign: 'right' }}>Accumulated</th>
                  <th style={{ textAlign: 'right' }}>Book value</th>
                </tr>
              </thead>
              <tbody>
                {preview.schedule.map((r, i) => (
                  <tr key={r.periodDate + i}>
                    <td>{i + 1}</td>
                    <td>{formatAccountingDate(r.periodDate)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.amount)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.accumulated)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.bookValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Projection only — nothing is posted until you save the asset.
          </p>
        </div>
      )}
    </div>
  );
}
